/** Server error envelope: `{ code | error, message }` with HTTP status. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: unknown;

  constructor(status: number, message: string, code: string | null = null, body: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  get isUnauthorized() {
    return this.status === 401;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409;
  }
  is(code: string) {
    return this.code === code || (typeof this.body === 'string' && this.body.includes(code));
  }
}

export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('NETWORK_ERROR');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** Parse `{code|error,message}` from a non-2xx response the way the mobile client does. */
export function apiErrorFromResponse(status: number, rawBody: string): ApiError {
  let json: unknown = null;
  try {
    json = rawBody ? JSON.parse(rawBody) : null;
  } catch {
    json = null;
  }
  const map = json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  const code = typeof map.code === 'string' ? map.code : typeof map.error === 'string' ? map.error : null;
  // VALIDACION / VALIDATION errors carry `details: [{field, message}]`; fold them into the text.
  const details = Array.isArray(map.details)
    ? (map.details as unknown[])
        .map((d) => (d && typeof d === 'object' ? [(d as Record<string, unknown>).field, (d as Record<string, unknown>).message].filter(Boolean).join(': ') : String(d)))
        .filter(Boolean)
        .join(' · ')
    : '';
  const base =
    (typeof map.message === 'string' && map.message) ||
    (typeof map.mensajes === 'string' && map.mensajes) ||
    (typeof map.mensaje === 'string' && map.mensaje) ||
    (typeof map.error === 'string' && map.error) ||
    (typeof map.detalle === 'string' && map.detalle) ||
    (typeof map.detail === 'string' && map.detail) ||
    `Request failed (${status})`;
  const message = details ? `${base} (${details})` : base;
  return new ApiError(status, message, code, json ?? rawBody);
}

/** Human message for toasts. */
export function errorMessage(err: unknown, fallback = 'Error desconocido'): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof NetworkError) return 'No se pudo conectar con el servidor.';
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
